// related spec doc: https://docs.google.com/document/d/11cvN92Akv-jInhYf82hXZJH0aHJP1y-feTb6Djem5RU

// https://www.npmjs.com/package/dding-fnv
import fnv from 'dding-fnv'

import { getCsrfToken } from './auth'

type PrecreateRes = {
  sequence: number // how many blocks that have been uploaded before
  blockbytes: number // each block size
}

type ErrorRes = {
  code: string
  error: boolean
  message: string
}

type CallbackFn = (err?: Error) => void

function fnvHash(body: string): string {
  return fnv.hash64(body, '1').toStr()
}

function genUuid(file: File): string {
  // same rule to generate uuid as https://github.com/pingcap/diag/pkg/packager/upload.go
  const fileNameHash = fnvHash(file.name)
  const sizeHash = fnvHash(file.size + '')
  const timeHash = fnvHash(new Date(file.lastModified).toISOString())
  return [fileNameHash, sizeHash, timeHash].join('-')
}

class FileUploader {
  static META_OFFSET = 8
  private metaLen: number = 0
  private payloadLen: number = 0
  private uuid: string = ''
  private blockSize: number = 0
  private preUploadedBlocks: number = 0
  private totalBlocks: number = 0
  private remainBlocks: number = -1
  private callbackFn?: CallbackFn
  private failUpload: boolean = false

  constructor(
    private file: File,
    private apiPathPrefix: string,
    private concurrency: number = 5
  ) {
    this.uuid = genUuid(file)
  }

  async preCheckFile() {
    // Check signature
    const signBlob = this.file.slice(0, 4)
    const signBuffer = await signBlob.arrayBuffer()
    const signArray = new Uint8Array(signBuffer)
    const signStr = signArray.reduce(
      (prev, cur) => prev + String.fromCharCode(cur),
      ''
    )
    if (signStr !== 'D1ag') {
      return Promise.reject(new Error(`It's not a valid diag package`))
    }

    // Check type
    // Currently only support type value is `00`
    const typeBlob = this.file.slice(4, 5)
    const typeBuffer = await typeBlob.arrayBuffer()
    const typeNumber = new Uint8Array(typeBuffer)
    if ((typeNumber[0] & 0b11000000) !== 0) {
      return Promise.reject(new Error(`Invalid diag package type`))
    }
  }

  async parseMeta() {
    const metaLenBlob = this.file.slice(4, 8)
    const metaLenBuffer = await metaLenBlob.arrayBuffer()
    const metaLenUint8Array = new Uint8Array(metaLenBuffer)
    metaLenUint8Array[0] = 0
    const metaLenUint32Array = new Uint32Array(
      metaLenUint8Array.reverse().buffer
    )
    this.metaLen = metaLenUint32Array[0]
    this.payloadLen = this.file.size - FileUploader.META_OFFSET - this.metaLen
  }

  uploadMetaData() {
    const metaData = this.file.slice(
      FileUploader.META_OFFSET,
      FileUploader.META_OFFSET + this.metaLen
    )

    const params = new URLSearchParams()
    params.append('uuid', this.uuid)
    params.append('length', String(this.payloadLen))
    params.append('filename', this.file.name)

    const xhr = new XMLHttpRequest()
    xhr.open('post', `${this.apiPathPrefix}/precreate?${params.toString()}`)
    xhr.setRequestHeader('x-csrf-token', getCsrfToken())
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')

    return new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status === 200) {
          const precreateRes = JSON.parse(xhr.response) as PrecreateRes
          this.computeBlocks(precreateRes)
          resolve({})
        } else {
          reject(this.parseErrorRes(xhr.response))
        }
      }
      xhr.send(metaData)
    })
  }

  computeBlocks(precreateRes: PrecreateRes) {
    this.blockSize = precreateRes.blockbytes
    this.preUploadedBlocks = precreateRes.sequence

    this.totalBlocks = Math.ceil(this.payloadLen / this.blockSize)
    this.remainBlocks = this.totalBlocks - this.preUploadedBlocks
  }

  uploadPayload() {
    if (this.remainBlocks <= 0) {
      this.flush()
      return
    }

    for (let i = 0; i < this.concurrency; i++) {
      this.uploadBlock(i + this.preUploadedBlocks)
    }
  }

  uploadBlock(seqNum: number, tries: number = 0) {
    if (this.failUpload) {
      return
    }
    if (seqNum > this.totalBlocks - 1) {
      return
    }

    let blobSize = this.blockSize
    if (seqNum === this.totalBlocks - 1) {
      blobSize = this.payloadLen - this.blockSize * seqNum
    }

    const blobSliceStart =
      FileUploader.META_OFFSET + this.metaLen + seqNum * this.blockSize
    const blobSliceEnd = blobSliceStart + blobSize
    const blob = this.file.slice(blobSliceStart, blobSliceEnd)

    const params = new URLSearchParams()
    params.append('uuid', this.uuid)
    params.append('length', String(blobSize))
    params.append('sequence', String(seqNum + 1))

    const xhr = new XMLHttpRequest()
    xhr.open('post', `${this.apiPathPrefix}/upload?${params.toString()}`)
    xhr.setRequestHeader('x-csrf-token', getCsrfToken())
    xhr.setRequestHeader('Content-Type', 'application/octet-stream')
    xhr.onload = () => {
      if (xhr.status === 200) {
        this.remainBlocks--
        if (this.remainBlocks === 0) {
          this.flush()
        } else {
          this.uploadBlock(seqNum + this.concurrency)
        }
      } else {
        // upload fail, retry it later
        if (tries < 2) {
          setTimeout(
            () => this.uploadBlock(seqNum, tries + 1),
            (tries + 1) * 1000
          )
        } else {
          if (!this.failUpload) {
            this.failUpload = true
            this.handleFail(this.parseErrorRes(xhr.response))
          }
        }
      }
    }
    xhr.send(blob)
  }

  flush() {
    const params = new URLSearchParams()
    params.append('uuid', this.uuid)

    const xhr = new XMLHttpRequest()
    xhr.open('post', `${this.apiPathPrefix}/flush?${params.toString()}`)
    xhr.setRequestHeader('x-csrf-token', getCsrfToken())
    xhr.onload = () => {
      if (xhr.status === 200) {
        this.handleSuccess()
      } else {
        this.handleFail(this.parseErrorRes(xhr.response))
      }
    }
    xhr.send()
  }

  parseErrorRes(res: any) {
    console.log(res)
    try {
      const errRes = JSON.parse(res) as ErrorRes
      return new Error(errRes.message)
    } catch {
      return new Error('upload failed')
    }
  }

  handleFail(err: Error) {
    this.callbackFn?.(err)
  }

  handleSuccess() {
    this.callbackFn?.()
  }

  async upload(callbackFn?: CallbackFn) {
    this.callbackFn = callbackFn

    try {
      await this.preCheckFile()
      await this.parseMeta()
      await this.uploadMetaData()
      this.uploadPayload()
    } catch (err) {
      console.log(err)
      this.handleFail(err as Error)
    }
  }
}

export default FileUploader
